import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, StoreProductOverride } from '../../services/api';

/** The query the product details dialog reads the store's override document from. */
export const storeProductOverrideQueryKey = (productId: string | null | undefined) => ['store-product-override', productId];

/**
 * Saves the store's own spec values (flag `store_override_specs_push`, MOT-413). The API replaces
 * `overrideSpecs` with the map sent, and the editor builds the next map from the cached override,
 * so the saved document is written into the cache on success, before the editor re-enables. A
 * second quick edit then starts from the first one's result instead of a stale map that would
 * erase it; the refetch that follows only confirms it.
 */
export function useStoreOwnSpecsSave(
  productId: string | null | undefined,
  feedback: { onSaved: () => void; onError: () => void },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (next: Record<string, string>) =>
      apiService.upsertStoreProductOverride(productId!, { overrideSpecs: Object.keys(next).length > 0 ? next : null }),
    onSuccess: async (saved: StoreProductOverride) => {
      const key = storeProductOverrideQueryKey(productId);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<StoreProductOverride | null | undefined>(key, (current) =>
        current ? { ...current, overrideSpecs: saved?.overrideSpecs ?? null } : saved,
      );
      void queryClient.invalidateQueries({ queryKey: key });
      feedback.onSaved();
    },
    onError: () => feedback.onError(),
  });
}
